import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getUserProfile, getSiteStatus } from '../services/api';

const UserContext = createContext();

/**
 * UserProvider
 *
 * Manages user authentication state, profile data, subscription tier, and feature access.
 * Provides helper methods to check feature access for frontend gating.
 *
 * Features:
 * - Fetches user profile on mount if token exists
 * - Provides hasFeatureAccess() method for checking feature permissions
 * - Tracks is_staff (admin) status for bypassing restrictions
 * - Caches profile data to avoid repeated API calls
 */
export const UserProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Whether sign-up is open (scale-down). null = not known yet; false if the call fails.
  const [registrationOpen, setRegistrationOpen] = useState(null);

  // Fetch user profile from API
  const fetchUserProfile = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      setUser(null);
      return;
    }

    try {
      setLoading(true);
      const profileData = await getUserProfile();
      setUser(profileData);
      setError(null);
    } catch (err) {
      console.error('Failed to fetch user profile:', err);
      setError(err.message || 'Failed to load user profile');
      // Don't clear user on error - might be temporary network issue
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch profile on mount
  useEffect(() => {
    fetchUserProfile();
  }, [fetchUserProfile]);

  // Fetch site status once on mount
  useEffect(() => {
    getSiteStatus()
      .then(data => setRegistrationOpen(data?.registration_open === true))
      .catch(() => setRegistrationOpen(false));
  }, []);

  // Listen for storage events (e.g., login/logout in another tab)
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token') {
        if (e.newValue) {
          // Token was added - fetch profile
          fetchUserProfile();
        } else {
          // Token was removed - clear user
          setUser(null);
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [fetchUserProfile]);

  /**
   * Check if user has access to a specific feature
   * @param {string} featureCode - The feature code to check (e.g., 'screener', 'watchlist')
   * @returns {boolean} - True if user has access, false otherwise
   */
  const hasFeatureAccess = useCallback((featureCode) => {
    // Not logged in - no access
    if (!user) {
      return false;
    }

    // Admin users (is_staff=true) have access to everything
    if (user.is_staff) {
      return true;
    }

    // Check if feature is in user's enabled features list
    if (user.features && Array.isArray(user.features)) {
      return user.features.includes(featureCode);
    }

    return false;
  }, [user]);

  /**
   * Refresh user profile (call after login or tier change)
   */
  const refreshProfile = useCallback(async () => {
    await fetchUserProfile();
  }, [fetchUserProfile]);

  /**
   * Clear user state (call on logout)
   */
  const clearUser = useCallback(() => {
    setUser(null);
    setError(null);
  }, []);

  const value = {
    // User state
    user,
    loading,
    error,

    // Helper methods
    hasFeatureAccess,
    refreshProfile,
    refreshUser: refreshProfile, // Alias for subscription components
    clearUser,

    // Convenience properties
    isAuthenticated: !!user,
    isAdmin: user?.is_staff || false,
    // Scale-down: false = show only the offline notice (Roles -> Feature Matrix -> App Access)
    hasAppAccess: !!user && (user.is_staff || user.has_app_access === true),
    registrationOpen,   // null until /site-status/ answers
    tier: user?.tier || null,
    tierDisplay: user?.tier_display || null,
    effectiveTier: user?.effective_tier || null,
    features: user?.features || [],
    canChangePassword: user?.can_change_password ?? false,

    // Privacy settings
    showUsername: user?.show_username ?? true,

    // Subscription properties
    hasSubscription: user?.has_subscription || false,
    stripeSubscriptionStatus: user?.stripe_subscription_status || null,
    subscriptionPeriodEnd: user?.subscription_period_end || null,
    cancelAtPeriodEnd: user?.cancel_at_period_end || false,

    // Account deletion properties
    deletionPending: !!user?.deletion_scheduled_at,
    deletionScheduledAt: user?.deletion_scheduled_at || null,
    deletionPermanentAt: user?.deletion_permanent_at || null,

    // Affiliate properties
    affiliateCode: user?.affiliate_code || null,
    hasAffiliateProfile: user?.has_affiliate_profile || false,
    referralCode: user?.referral_code || null,
  };

  return (
    <UserContext.Provider value={value}>
      {children}
    </UserContext.Provider>
  );
};

/**
 * useUser Hook
 *
 * Access the user context state and methods
 *
 * @example
 * const { user, hasFeatureAccess, isAdmin } = useUser();
 * if (hasFeatureAccess('dashboard_builder')) {
 *   // Show dashboard builder
 * }
 */
export const useUser = () => {
  const context = useContext(UserContext);
  if (!context) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};

export default UserContext;
